/**
 * compose-deps — **ประกอบ port → adapter** (ครั้งเดียวตอน boot)
 *
 * อ่านไฟล์นี้แล้วต้องเห็นทั้งฝั่ง client ในหน้าเดียว: port ตัวไหนถูกเติมด้วยอะไร
 * (คู่ขนานกับ `composition-root.ts` ของ `member--webapi`)
 *
 * 🔑 **สัญญาไม่ได้อยู่ที่นี่** — `RouteDeps` เป็นของ `routes/` ซึ่งเป็นฝ่าย *ใช้*
 *    (PLAN §1 ข้อ 4) · ไฟล์นี้อ่านสัญญาจากที่นั่นแล้วไปหาของมาเติมให้ครบ
 *    ⇒ เพิ่ม port ใหม่ = แก้ที่ `routes/index.tsx` แล้ว TypeScript มาฟ้องที่นี่
 *      **ไม่ใช่กลับกัน**
 *
 * 🔴 **นี่คือที่เดียวในแอปที่แตะ `window`** (`__ELITE_CONFIG__` + `location.origin`)
 *    lib ทุกตัวรับค่าทาง param ⇒ วันย้ายไป SSR สิ่งที่ต้องเขียนใหม่คือไฟล์นี้ไฟล์เดียว
 *    (รายละเอียดว่าทำไมโครงแก้ rendering model ให้ฟรีไม่ได้ → PLAN §0)
 */
import {
  createActivitiesApi,
  createAuthApi,
  createLiffAdapter,
  createShareAdapter,
} from '@theelite/frontend-member-adapters';
import { pathOf } from '@theelite/frontend-member-feature-auth';
import {
  createClipboardAdapter,
  createGoogleIdentityAdapter,
  createNavigation,
  createSessionStore,
  loadWebConfig,
  type WebConfigSource,
} from '@theelite/frontend-shared-web';
import type { RouteDeps } from '../routes/index.js';

/**
 * รูปที่ `/config.js` ตั้งไว้บน `window` — ตรงกับ `config.template.js` (ADR-010)
 *
 * ⚠️ **`declare global` อยู่ที่แอป ไม่ใช่ที่ lib** — `window.__ELITE_CONFIG__` เป็น
 *    กลไกของ **เว็บที่เสิร์ฟแบบ static** · lib ที่ประกาศ global ไว้เอง = ลาก
 *    สมมติฐานเรื่องเบราว์เซอร์ไปทุกที่ที่ import มัน (รวมถึงฝั่ง server วันที่ย้าย SSR)
 */
declare global {
  interface Window {
    __ELITE_CONFIG__?: WebConfigSource;
  }
}

/**
 * ⚠️ รับ `navigate` เข้ามา **ไม่สร้าง router เอง** — composition root ไม่ใช่ component
 *    จึงเรียก `useNavigate()` ไม่ได้ · ลำดับการประกอบอยู่ `composition/index.ts`
 */
export function composeApp(navigate: (path: string) => void): RouteDeps {
  const config = loadWebConfig(
    typeof window === 'undefined' ? undefined : window.__ELITE_CONFIG__,
  );

  /**
   * ⚠️ **ต้องสร้างก่อน `activitiesApi`** — Day 5 ทำให้ adapter ของกิจกรรมต้องอ่าน
   *    access token ได้ (endpoint ลงทะเบียนเป็น `auth: 'bearer'`)
   *    ⚠️ instance เดียวต่อการโหลดหน้า — สร้างที่นี่เท่านั้น (ห้ามอยู่ใน lib)
   */
  const session = createSessionStore();

  return {
    liff: createLiffAdapter(config.liffId),
    google:
      config.googleClientId === undefined
        ? undefined
        : createGoogleIdentityAdapter(config.googleClientId),
    authApi: createAuthApi(config.apiBaseUrl),
    /**
     * ส่ง **ฟังก์ชันอ่าน token** ไม่ใช่ตัว token — token เปลี่ยนระหว่างอายุของแอปได้
     * (login / refresh / logout) · จับค่าไว้ตอนนี้ = ใช้ใบที่หมดอายุไปแล้ว
     */
    activitiesApi: createActivitiesApi(config.apiBaseUrl, () => session.get()),
    clipboard: createClipboardAdapter(),
    share: createShareAdapter(),
    /**
     * 🔑 `createNavigation` ของ `shared-web` **ไม่รู้จัก `AppRoute`** — มันรับวิธีแปลง
     *    route เป็น path เข้าไป · `pathOf` อยู่ข้าง `AppRoute` ใน `feature-auth`
     *    ⇒ เพิ่ม route ใหม่แล้วลืมแปลง TypeScript ฟ้องที่ feature ไม่ใช่ที่แอป
     */
    navigation: createNavigation(navigate, pathOf),
    onAuthenticated: (accessToken) => session.set(accessToken),
    webOrigin: window.location.origin,
  };
}
